import { catchError, finalize, map, of, switchMap } from 'rxjs';

import { computed, inject } from '@angular/core';

import { SystemUnit, unitUtilsFactory } from '@mitsubishi/project/data-access';
import {
  OutputConfiguration,
  OutputItemRequest,
  OutputsService,
  PerformanceAccessory,
  PerformanceEnvelop,
  PerformanceMessage,
  UnitPerformanceDataResponse,
} from '@mitsubishi/shared/data-access';
import { MiscUtils, setCompletedState, setPendingState, withRequestStatus } from '@mitsubishi/utils';

import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

import { EnergyAnalysisService } from '../../services/energy-analysis.service';

export interface SystemUnitPerformanceState {
  performanceData: UnitPerformanceDataResponse | undefined;
  outputConfigurations: OutputConfiguration[];
}

const initialState: SystemUnitPerformanceState = {
  performanceData: undefined,
  outputConfigurations: [],
};

export const SystemUnitPerformanceStore = signalStore(
  withState(initialState),
  withRequestStatus(),
  withComputed(({ performanceData }) => ({
    selectedPerformance: computed(() => performanceData()?.performances.find(perf => perf.selected)),
    performanceAccessories: computed(() =>
      MiscUtils.getObjectValues<PerformanceAccessory>(performanceData()?.performanceAccessories)
    ),
    performanceEnvelopes: computed(() =>
      MiscUtils.getObjectValues<PerformanceEnvelop>(performanceData()?.performanceEnvelopes)
    ),
    hasPerformanceAccessories: computed(() => {
      const accessories = performanceData()?.performanceAccessories;
      return accessories ? Object.keys(accessories).length > 0 : false;
    }),
  })),
  withComputed(({ selectedPerformance }, unitUtils = unitUtilsFactory()) => ({
    performanceMessages: computed(() =>
      MiscUtils.getObjectValues<PerformanceMessage>(selectedPerformance()?.performanceMessages)
    ),
    hasPerformanceErrorMessages: computed(() => {
      const currentSelectedPerformance = selectedPerformance();
      if (!currentSelectedPerformance) {
        return true;
      }

      return unitUtils.hasPerformanceErrorMessages(
        MiscUtils.getObjectValues(currentSelectedPerformance.performanceMessages)
      );
    }),
  })),
  withMethods(
    (store, energyAnalysisService = inject(EnergyAnalysisService), outputsService = inject(OutputsService)) => ({
      /**
       * Loads performance data for a unit and output configurations separately
       */
      loadPerformanceData: ({ projectId, unit }: { projectId: string; unit: SystemUnit }) => {
        patchState(store, setPendingState());
        const outputRequest = createOutputRequest(unit);

        energyAnalysisService
          .getUnitPerformanceResult(unit.id)
          .pipe(
            switchMap(performanceData =>
              outputsService.getOutputsConfiguration(projectId, [outputRequest]).pipe(
                map(outputConfigurations => {
                  return { performanceData, outputConfigurations };
                }),
                catchError(error => {
                  console.warn(
                    'Failed to load output configurations, using performance data without configurations:',
                    error
                  );
                  return of({ performanceData, outputConfigurations: [] });
                })
              )
            ),
            finalize(() => patchState(store, setCompletedState()))
          )
          .subscribe({
            next: ({ performanceData, outputConfigurations }) => {
              patchState(store, { performanceData, outputConfigurations });
            },
          });
      },
    })
  )
);

/**
 * Creates output request information from unit data
 */
function createOutputRequest(unit: SystemUnit): OutputItemRequest {
  return {
    segmentId: unit.segmentId,
    categoryCode: unit.categoryCode || '',
    productTypeId: unit.productTypeId,
  };
}
